import { useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Save, Palette, Check, X, Library, Upload, Heart, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const FONT_SCALES = [
  { v: 0.94, label: "Compact (94%)" },
  { v: 1.0, label: "Default (100%)" },
  { v: 1.06, label: "Comfortable (106%)" },
  { v: 1.12, label: "Spacious (112%)" },
];
const ACCENT_PRESETS = [
  { hsl: "16 100% 50%", label: "Sunset (default)" },
  { hsl: "221 83% 53%", label: "Cobalt" },
  { hsl: "142 71% 45%", label: "Emerald" },
  { hsl: "280 65% 55%", label: "Royal" },
  { hsl: "350 80% 56%", label: "Crimson" },
  { hsl: "30 95% 55%", label: "Amber" },
];

const FONT_OPTIONS = [
  "", "Inter", "Bricolage Grotesque", "Playfair Display",
  "Space Grotesk", "IBM Plex Sans", "IBM Plex Mono",
  "DM Serif Display", "Manrope", "Poppins", "Roboto",
  "Nunito", "Merriweather", "JetBrains Mono",
];

/**
 * AppearanceSection — full theme editor.
 * Sits inside the Settings page and covers:
 *   • Preset gallery (system-wide)
 *   • Font scale + density
 *   • Accent-colour picker
 *   • Advanced (fonts, radius, shadows, backgrounds, buttons, tables,
 *     per-component tints)
 *
 * All changes fan out through AppearanceApplier via CSS variables/data-attrs.
 */
export default function AppearanceSection() {
  const { activeWorkspaceId, activeWorkspace, refresh } = useAuth();
  const initial = activeWorkspace?.appearance || {};
  const [scale, setScale] = useState(initial.font_scale || 1);
  const [density, setDensity] = useState(initial.density_default || "comfortable");
  const [accent, setAccent] = useState(initial.accent_hsl || "");
  // Advanced
  const [headingFont, setHeadingFont] = useState(initial.heading_font || "");
  const [bodyFont, setBodyFont] = useState(initial.body_font || "");
  const [headingScale, setHeadingScale] = useState(initial.heading_scale || 1);
  const [borderRadius, setBorderRadius] = useState(typeof initial.border_radius === "number" ? initial.border_radius : 0);
  const [shadowStyle, setShadowStyle] = useState(initial.shadow_style || "");
  const [backgroundStyle, setBackgroundStyle] = useState(initial.background_style || "");
  const [buttonStyle, setButtonStyle] = useState(initial.button_style || "");
  const [tableStyle, setTableStyle] = useState(initial.table_style || "");
  const [headerHsl, setHeaderHsl] = useState(initial.header_hsl || "");
  const [sidebarHsl, setSidebarHsl] = useState(initial.sidebar_hsl || "");
  const [cardHsl, setCardHsl] = useState(initial.card_hsl || "");
  const [surfaceHsl, setSurfaceHsl] = useState(initial.surface_hsl || "");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [presets, setPresets] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const a = activeWorkspace?.appearance || {};
    setScale(a.font_scale || 1);
    setDensity(a.density_default || "comfortable");
    setAccent(a.accent_hsl || "");
    setHeadingFont(a.heading_font || "");
    setBodyFont(a.body_font || "");
    setHeadingScale(a.heading_scale || 1);
    setBorderRadius(typeof a.border_radius === "number" ? a.border_radius : 0);
    setShadowStyle(a.shadow_style || "");
    setBackgroundStyle(a.background_style || "");
    setButtonStyle(a.button_style || "");
    setTableStyle(a.table_style || "");
    setHeaderHsl(a.header_hsl || "");
    setSidebarHsl(a.sidebar_hsl || "");
    setCardHsl(a.card_hsl || "");
    setSurfaceHsl(a.surface_hsl || "");
  }, [activeWorkspace?.id, activeWorkspace?.appearance]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/appearance/presets");
        setPresets(data.presets || []);
      } catch { /* silent */ }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/appearance`, {
        font_scale: scale, density_default: density, accent_hsl: accent || null,
        heading_font: headingFont || null, body_font: bodyFont || null,
        heading_scale: headingScale, border_radius: borderRadius,
        shadow_style: shadowStyle || null, background_style: backgroundStyle || null,
        button_style: buttonStyle || null, table_style: tableStyle || null,
        header_hsl: headerHsl || null, sidebar_hsl: sidebarHsl || null,
        card_hsl: cardHsl || null, surface_hsl: surfaceHsl || null,
      });
      toast.success("Appearance saved");
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  const applyPreset = async (pid) => {
    if (!window.confirm("Apply this preset? Your current appearance customisations will be replaced.")) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/appearance/apply-preset/${pid}`);
      toast.success("Preset applied");
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };

  // ─── Preview mode ───
  // When the user hovers a preset (or edits a control), we paint the workspace
  // live via CSS variables without touching the DB. A sticky "Keep / Revert"
  // bar surfaces so the change is committed intentionally.
  const [previewingId, setPreviewingId] = useState(null);
  const [previewConfig, setPreviewConfig] = useState(null);  // committed preview
  const hoverTimer = useRef(null);

  const injectPreview = useCallback((cfg) => {
    const root = document.documentElement;
    const HSL = /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/;
    const setHsl = (name, v) => {
      if (v && HSL.test(v)) root.style.setProperty(name, v);
      else root.style.removeProperty(name);
    };
    root.style.setProperty("--font-scale", String(cfg.font_scale || 1));
    setHsl("--primary", cfg.accent_hsl);
    setHsl("--ring", cfg.accent_hsl);
    if (cfg.heading_font) {
      const id = `gf-${cfg.heading_font.replace(/\s+/g, "-").toLowerCase()}`;
      if (!document.getElementById(id)) {
        const link = document.createElement("link"); link.id = id; link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(cfg.heading_font)}:wght@300;400;500;600;700;800&display=swap`;
        document.head.appendChild(link);
      }
      root.style.setProperty("--heading-font", `"${cfg.heading_font}"`);
    } else root.style.removeProperty("--heading-font");
    if (cfg.body_font) {
      const id = `gf-${cfg.body_font.replace(/\s+/g, "-").toLowerCase()}`;
      if (!document.getElementById(id)) {
        const link = document.createElement("link"); link.id = id; link.rel = "stylesheet";
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(cfg.body_font)}:wght@300;400;500;600;700;800&display=swap`;
        document.head.appendChild(link);
      }
      root.style.setProperty("--body-font", `"${cfg.body_font}"`);
    } else root.style.removeProperty("--body-font");
    if (cfg.heading_scale) root.style.setProperty("--heading-scale", String(cfg.heading_scale));
    else root.style.removeProperty("--heading-scale");
    if (typeof cfg.border_radius === "number") root.style.setProperty("--radius", `${cfg.border_radius}px`);
    else root.style.removeProperty("--radius");
    root.dataset.shadowStyle = cfg.shadow_style || "";
    root.dataset.backgroundStyle = cfg.background_style || "";
    root.dataset.buttonStyle = cfg.button_style || "";
    root.dataset.tableStyle = cfg.table_style || "";
    setHsl("--surface", cfg.surface_hsl);
    setHsl("--header-bg", cfg.header_hsl);
    setHsl("--sidebar-bg", cfg.sidebar_hsl);
    setHsl("--card-bg", cfg.card_hsl);
  }, []);

  const restoreLive = useCallback(() => {
    // Restore whatever the workspace's saved appearance is.
    injectPreview(activeWorkspace?.appearance || {});
  }, [injectPreview, activeWorkspace?.appearance]);

  const startHoverPreview = (preset) => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      if (!previewConfig) injectPreview(preset.config);
    }, 120);
  };
  const endHoverPreview = () => {
    clearTimeout(hoverTimer.current);
    if (!previewConfig) restoreLive();
  };

  const commitPreview = (preset) => {
    setPreviewingId(preset.id);
    setPreviewConfig(preset.config);
    injectPreview(preset.config);
  };

  const keepPreview = async () => {
    if (!previewingId) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/appearance/apply-preset/${previewingId}`);
      toast.success("Theme applied");
      setPreviewingId(null);
      setPreviewConfig(null);
      await refresh();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
    finally { setBusy(false); }
  };

  const revertPreview = () => {
    setPreviewingId(null);
    setPreviewConfig(null);
    restoreLive();
  };

  const [libraryOpen, setLibraryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  useEffect(() => {
    // Clean up any preview state when unmounting.
    return () => { restoreLive(); };
  }, [restoreLive]);

  return (
    <div className="border border-border p-6 space-y-4" data-testid="settings-appearance">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Palette className="w-4 h-4 text-primary" />
          <div className="label-eyebrow">Appearance</div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setLibraryOpen(true)} data-testid="btn-open-theme-library">
            <Library className="w-3.5 h-3.5 mr-1" /> Community themes
          </Button>
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => setPublishOpen(true)} data-testid="btn-open-publish-theme">
            <Upload className="w-3.5 h-3.5 mr-1" /> Publish this theme
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Workspace-wide look & feel. Start with a preset, tune fonts / borders / backgrounds / buttons / tables, or browse themes published by other Acquilix workspaces.
      </p>

      {/* Preset gallery */}
      <div>
        <div className="label-eyebrow mb-2">Presets — hover to preview, click to commit</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="appearance-presets">
          {presets.map((p) => (
            <button key={p.id}
              onClick={() => commitPreview(p)}
              onMouseEnter={() => startHoverPreview(p)}
              onMouseLeave={endHoverPreview}
              onFocus={() => startHoverPreview(p)}
              onBlur={endHoverPreview}
              disabled={busy}
              className={`border p-3 text-left transition ${previewingId === p.id ? "border-primary ring-2 ring-primary/40" : "border-border hover:border-primary"}`}
              data-testid={`appearance-preset-${p.id}`}>
              <div className="font-semibold text-sm">{p.name}</div>
              <div className="text-[11px] text-muted-foreground">{p.description}</div>
              <div className="flex gap-1 mt-2">
                <span className="w-4 h-4 rounded-full border" style={{ background: `hsl(${p.config.accent_hsl})` }} />
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground ml-1">
                  {p.config.heading_font?.slice(0, 12) || "system"}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Preview action bar — sticky at bottom until confirmed */}
      {previewingId && (
        <div className="fixed inset-x-0 bottom-0 z-50 bg-foreground text-background px-6 py-3 flex items-center justify-between shadow-2xl border-t-4 border-primary"
             data-testid="preview-action-bar">
          <div className="text-sm">
            <strong className="text-primary">Previewing:</strong>{" "}
            {presets.find((p) => p.id === previewingId)?.name || previewingId}
            <span className="ml-3 text-xs opacity-70">Nothing is saved until you press Keep.</span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="rounded-none text-background hover:text-background hover:bg-background/10" onClick={revertPreview} data-testid="preview-revert-btn">
              <X className="w-4 h-4 mr-1" /> Revert
            </Button>
            <Button size="sm" className="rounded-none" onClick={keepPreview} disabled={busy} data-testid="preview-keep-btn">
              <Check className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Keep this theme"}
            </Button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="label-eyebrow mb-2 inline-block">Font size</Label>
          <Select value={String(scale)} onValueChange={(v) => setScale(Number(v))}>
            <SelectTrigger className="h-10" data-testid="appearance-font-scale"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FONT_SCALES.map((s) => <SelectItem key={s.v} value={String(s.v)}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow mb-2 inline-block">Default density</Label>
          <Select value={density} onValueChange={setDensity}>
            <SelectTrigger className="h-10" data-testid="appearance-density"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="comfortable">Comfortable</SelectItem>
              <SelectItem value="compact">Compact</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="label-eyebrow mb-2 inline-block">Accent colour</Label>
        <div className="flex flex-wrap gap-2" data-testid="appearance-accent-presets">
          {ACCENT_PRESETS.map((p) => (
            <button
              key={p.hsl}
              type="button"
              onClick={() => setAccent(p.hsl)}
              className={`px-3 py-1.5 border text-xs rounded-full inline-flex items-center gap-2 ${accent === p.hsl ? "border-foreground" : "border-border"}`}
              data-testid={`appearance-accent-${p.hsl.split(" ")[0]}`}
            >
              <span className="w-3 h-3 rounded-full" style={{ background: `hsl(${p.hsl})` }} />
              {p.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setAccent("")}
            className={`px-3 py-1.5 border text-xs rounded-full ${!accent ? "border-foreground" : "border-border"}`}
            data-testid="appearance-accent-reset"
          >
            Reset to default
          </button>
        </div>
        <Input
          value={accent}
          onChange={(e) => setAccent(e.target.value)}
          placeholder='Custom HSL e.g. "210 80% 50%"'
          className="h-10 mt-3 font-mono text-xs"
          data-testid="appearance-accent-input"
        />
      </div>

      {/* Advanced */}
      <div className="border-t border-border pt-4">
        <button onClick={() => setShowAdvanced((s) => !s)}
                className="text-sm font-semibold text-primary flex items-center gap-1"
                data-testid="appearance-advanced-toggle">
          {showAdvanced ? "▼" : "▶"} Advanced customisation (fonts, radius, backgrounds, buttons, tables, per-component tints)
        </button>
      </div>
      {showAdvanced && (
        <div className="space-y-4 pt-2" data-testid="appearance-advanced">
          <div className="grid sm:grid-cols-2 gap-4">
            <FontSelect label="Heading font" value={headingFont} onChange={setHeadingFont} testid="ap-heading-font" />
            <FontSelect label="Body font" value={bodyFont} onChange={setBodyFont} testid="ap-body-font" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Heading scale ({headingScale}×)</Label>
              <input type="range" min="0.85" max="1.6" step="0.05" value={headingScale}
                     onChange={(e) => setHeadingScale(Number(e.target.value))}
                     className="w-full" data-testid="ap-heading-scale" />
            </div>
            <div>
              <Label className="label-eyebrow mb-2 inline-block">Border radius ({borderRadius}px)</Label>
              <input type="range" min="0" max="24" step="1" value={borderRadius}
                     onChange={(e) => setBorderRadius(Number(e.target.value))}
                     className="w-full" data-testid="ap-border-radius" />
            </div>
            <SmallSelect label="Shadow" value={shadowStyle} onChange={setShadowStyle} testid="ap-shadow"
              options={[["", "Default"], ["none", "None"], ["subtle", "Subtle"], ["soft", "Soft"], ["hard", "Hard (brutalist)"]]} />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <SmallSelect label="Background style" value={backgroundStyle} onChange={setBackgroundStyle} testid="ap-bg-style"
              options={[["", "Solid"], ["noise", "Subtle noise"], ["dots", "Dot grid"], ["grid", "Line grid"]]} />
            <SmallSelect label="Button style" value={buttonStyle} onChange={setButtonStyle} testid="ap-btn-style"
              options={[["", "Solid"], ["gradient", "Gradient"], ["outline", "Outline"], ["subtle", "Subtle"]]} />
            <SmallSelect label="Table style" value={tableStyle} onChange={setTableStyle} testid="ap-table-style"
              options={[["", "Default"], ["striped", "Striped rows"], ["bordered", "Bordered"], ["minimal", "Minimal"]]} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <HslField label="Surface (page background) HSL" value={surfaceHsl} onChange={setSurfaceHsl} testid="ap-surface" />
            <HslField label="Header HSL" value={headerHsl} onChange={setHeaderHsl} testid="ap-header" />
            <HslField label="Sidebar HSL" value={sidebarHsl} onChange={setSidebarHsl} testid="ap-sidebar" />
            <HslField label="Card HSL" value={cardHsl} onChange={setCardHsl} testid="ap-card" />
          </div>
        </div>
      )}

      <Button onClick={save} disabled={busy} className="rounded-none" data-testid="appearance-save-btn">
        <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save appearance"}
      </Button>

      {libraryOpen && (
        <ThemeLibraryDialog
          wsId={activeWorkspaceId}
          onClose={() => setLibraryOpen(false)}
          onImported={async () => { setLibraryOpen(false); await refresh(); }}
        />
      )}
      {publishOpen && (
        <PublishThemeDialog
          wsId={activeWorkspaceId}
          onClose={() => setPublishOpen(false)}
        />
      )}
    </div>
  );
}

function FontSelect({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full border border-border bg-background px-2 text-sm" data-testid={testid}>
        {FONT_OPTIONS.map((o) => <option key={o} value={o}>{o || "— system default —"}</option>)}
      </select>
    </div>
  );
}

function SmallSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-10 w-full border border-border bg-background px-2 text-sm" data-testid={testid}>
        {options.map(([v, lbl]) => <option key={v} value={v}>{lbl}</option>)}
      </select>
    </div>
  );
}

function HslField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <div className="flex items-center gap-2">
        <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
               placeholder='e.g. "222 47% 11%"'
               className="h-10 flex-1 border border-border bg-background px-2 text-xs font-mono" data-testid={testid} />
        {value && <span className="w-8 h-8 border border-border" style={{ background: `hsl(${value})` }} />}
      </div>
    </div>
  );
}

// ─────────────── Community Theme Library ───────────────

function ThemeLibraryDialog({ wsId, onClose, onImported }) {
  const [themes, setThemes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("popular");

  useEffect(() => {
    // AbortController prevents a stale-response race on rapid open/close.
    const ctrl = new AbortController();
    (async () => {
      setBusy(true); setError(null);
      try {
        const params = { sort, limit: 50 };
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/theme-library", { params, signal: ctrl.signal });
        setThemes(data.themes || []);
      } catch (e) {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          setError(apiErrorMessage(e, "Failed to load themes"));
        }
      } finally { setBusy(false); }
    })();
    return () => ctrl.abort();
  }, [sort, wsId]);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const params = { sort, limit: 50 };
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/theme-library", { params });
      setThemes(data.themes || []);
    } catch (e) { setError(apiErrorMessage(e, "Failed to load themes")); }
    finally { setBusy(false); }
  };

  const importTheme = async (t) => {
    if (!window.confirm(`Import "${t.name}"? Your current appearance customisations will be replaced.`)) return;
    try {
      await api.post(`/workspaces/${wsId}/theme-library/import/${t.id}`);
      toast.success(`Imported "${t.name}"`);
      onImported && onImported();
    } catch (e) { toast.error(apiErrorMessage(e, "Import failed")); }
  };

  const toggleLike = async (t) => {
    try {
      const { data } = await api.post(`/theme-library/${t.id}/like`);
      setThemes((prev) => prev.map((x) => x.id === t.id
        ? { ...x, liked_by_me: data.liked, like_count: (x.like_count || 0) + (data.liked ? 1 : -1) }
        : x));
    } catch (e) { toast.error(apiErrorMessage(e, "Like failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="theme-library-dialog"
                     description="Browse workspace themes published by the Acquilix community. Preview, like, and import into your workspace.">
        <DialogHeader><DialogTitle>Community theme library</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2 flex-wrap border-b border-border pb-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()}
                 placeholder="Search themes…" className="h-9 rounded-none max-w-xs" data-testid="tl-search" />
          <Button size="sm" variant="outline" className="rounded-none h-9" onClick={load}>Search</Button>
          <div className="ml-auto flex items-center gap-1 border border-border p-0.5">
            {["popular", "recent", "likes"].map((s) => (
              <button key={s} onClick={() => setSort(s)}
                className={`px-2 py-1 text-xs uppercase tracking-widest ${sort === s ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`tl-sort-${s}`}>{s}</button>
            ))}
          </div>
        </div>

        {busy ? (
          <div className="text-sm text-muted-foreground py-6">Loading…</div>
        ) : error ? (
          <div className="border border-red-300 bg-red-50 text-red-800 p-4 text-sm" data-testid="tl-error">{error}</div>
        ) : themes.length === 0 ? (
          <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            <Library className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No themes yet. Be the first — publish yours.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="tl-list">
            {themes.map((t) => (
              <div key={t.id} className="border border-border p-4 flex gap-3" data-testid={`tl-card-${t.id}`}>
                <div className="w-14 h-14 border border-border relative overflow-hidden shrink-0"
                     style={{
                       background: `hsl(${t.config?.surface_hsl || "0 0% 100%"})`,
                     }}>
                  <span className="absolute inset-0 flex items-center justify-center text-lg font-bold"
                        style={{ color: `hsl(${t.config?.accent_hsl || "16 100% 50%"})`,
                                  fontFamily: t.config?.heading_font ? `"${t.config.heading_font}"` : undefined }}>
                    Aa
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm truncate">{t.name}</span>
                    <span className="text-[10px] uppercase tracking-widest border border-border px-1.5 py-0.5 text-muted-foreground">
                      by {t.published_by_workspace_name || "anon"}
                    </span>
                  </div>
                  {t.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</p>}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(t.tags || []).slice(0, 4).map((tag) => (
                      <span key={tag} className="text-[10px] uppercase tracking-widest border border-primary/30 text-primary px-1.5 py-0.5">{tag}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                    <span>📥 {t.install_count} installs</span>
                    <button onClick={() => toggleLike(t)} className={`inline-flex items-center gap-0.5 hover:text-red-600 ${t.liked_by_me ? "text-red-600" : ""}`} data-testid={`tl-like-${t.id}`}>
                      <Heart className={`w-3 h-3 ${t.liked_by_me ? "fill-current" : ""}`} /> {t.like_count}
                    </button>
                    <Button size="sm" variant="outline" className="rounded-none h-7 ml-auto" onClick={() => importTheme(t)} data-testid={`tl-import-${t.id}`}>
                      Import
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PublishThemeDialog({ wsId, onClose }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (name.trim().length < 3) { toast.error("Name must be at least 3 characters"); return; }
    setBusy(true);
    try {
      const tagArr = tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean).slice(0, 6);
      await api.post(`/workspaces/${wsId}/theme-library/publish`, {
        name: name.trim(), description: description.trim(),
        visibility, tags: tagArr,
      });
      toast.success("Theme published to the community library");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="publish-theme-dialog"
                     description="Snapshot the current workspace appearance and publish it to the community theme library.">
        <DialogHeader><DialogTitle>Publish this appearance as a shareable theme</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">
          Snapshots your current appearance settings and publishes them to the Acquilix community library. Other workspaces can preview + import your theme.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Theme name *</Label>
            <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. Studio Sunset Warm" className="h-10 rounded-none" data-testid="pt-name" />
            <div className="text-[10px] text-muted-foreground text-right">{name.length}/80</div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Description</Label>
            <Textarea rows={3} value={description} maxLength={400} onChange={(e) => setDescription(e.target.value)}
                      placeholder="A brief description of the mood / vibe / intended use" className="rounded-none text-sm" data-testid="pt-description" />
            <div className="text-[10px] text-muted-foreground text-right">{description.length}/400</div>
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Tags (comma-separated, max 6)</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)}
                   placeholder="dark, minimal, editorial, festive" className="h-10 rounded-none font-mono text-xs" data-testid="pt-tags" />
          </div>
          <div>
            <Label className="label-eyebrow mb-1 inline-block">Visibility</Label>
            <div className="flex gap-2">
              {[
                ["public", "Public (searchable)"],
                ["unlisted", "Unlisted (link only)"],
              ].map(([v, lbl]) => (
                <button key={v} onClick={() => setVisibility(v)}
                        className={`px-3 py-1.5 text-xs border ${visibility === v ? "border-foreground bg-foreground text-background" : "border-border"}`}
                        data-testid={`pt-vis-${v}`}>{lbl}</button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy || name.trim().length < 3} data-testid="pt-submit">
            <Upload className="w-4 h-4 mr-1" /> {busy ? "Publishing…" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

